<template>
  <div class="box">
    <div class="mindmap-page">
      <div id="mindmap-container">
        <div id="nodes-container">
          <!-- 中心节点 -->
          <div class="node" id="center" :style="getCenterNodeStyle" @click="handleNodeClick(nodeData.center)"
               @mouseleave="handleMouseLeave" ref="centerNode">
            {{ nodeData.center.text }}
          </div>
          <!-- 核心功能节点 -->
          <div v-for="(core, index) in nodeData.core" :key="core.id" class="node core-node" :id="core.id"
               :style="getNodePosition(core)" @click="handleNodeClick(core)" @mouseleave="handleMouseLeave"
               :ref="`node-${core.id}`">
            {{ core.text }}
          </div>
          <!-- 次级功能节点 -->
          <div v-for="(sec, index) in nodeData.secondary" :key="sec.id" class="node secondary-node" :id="sec.id"
               :style="getNodePosition(sec)" @click="handleNodeClick(sec)" @mouseleave="handleMouseLeave"
               :ref="`node-${sec.id}`">
            {{ sec.text }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'HrpHierarchyMindmap',
  data() {
    /**
     * 计算椭圆坐标
     * 根据椭圆参数方程 x = a*cosθ, y = b*sinθ 计算均匀分布的位置
     */
    const calculateEllipsePositions = (count, radiusX, radiusY, startAngle = 0) => {
      const positions = [];
      const angleStep = (2 * Math.PI) / count; // 每个节点的角度间隔

      for (let i = 0; i < count; i++) {
        const angle = startAngle + i * angleStep;
        const x = Math.round(radiusX * Math.cos(angle));
        const y = Math.round(radiusY * Math.sin(angle));
        positions.push({x, y});
      }

      return positions;
    };

    // 生成15个次级节点坐标（外层椭圆）
    const secondaryPositions = calculateEllipsePositions(15, 400, 251, -Math.PI / 2);
    // 生成5个核心节点坐标（内层椭圆）
    const corePositions = calculateEllipsePositions(5, 220, 130, -Math.PI / 2);

    return {
      // 动画是否已启动（防止重复触发）
      animationStarted: false,
      // 节点数据配置
      nodeData: {
        // 中心节点
        center: {
          id: 'center',
          text: '敬在HRP',
          x: 0, // 中心点坐标设为(0,0)
          y: 0,
          url: '/hprSystem?name=HRP智慧管理平台'
        },
        // 核心节点（黑底白字）
        core: [
          {
            id: 'core1',
            text: 'DRG/DIP成本管理',
            x: corePositions[0].x,
            y: corePositions[0].y,
            url: '/department?name=病种(DRG/DIP)成本管理'
          },
          {
            id: 'core2',
            text: '全面绩效管理',
            x: corePositions[1].x,
            y: corePositions[1].y,
            url: '/Overall?name=全面绩效管理'
          },
          {
            id: 'core3',
            text: '全面预算管控',
            x: corePositions[2].x,
            y: corePositions[2].y,
            url: '/bascm?name=全面预算管理'
          },
          {id: 'core4', text: '人工智能+', x: corePositions[3].x, y: corePositions[3].y, url: '/AISmartManagement'},
          {
            id: 'core5',
            text: '医院运营数据中心',
            x: corePositions[4].x,
            y: corePositions[4].y,
            url: '/odrSystem?name=ODR系统'
          },
        ],
        // 次级节点（白底黑字）
        secondary: [
          {
            id: 'sec1',
            text: '资金监管',
            x: secondaryPositions[0].x,
            y: secondaryPositions[0].y,
            url: '/finance?name=资金监管'
          },
          {
            id: 'sec2',
            text: '电子会计档案',
            x: secondaryPositions[1].x,
            y: secondaryPositions[1].y,
            url: '/finance?name=电子会计档案'
          },
          {
            id: 'sec3',
            text: '智能费用报销',
            x: secondaryPositions[2].x,
            y: secondaryPositions[2].y,
            url: '/bascm?name=费用报销管理'
          },
          {
            id: 'sec4',
            text: '科室成本核算',
            x: secondaryPositions[3].x,
            y: secondaryPositions[3].y,
            url: '/department?name=科室成本管理'
          },
          {
            id: 'sec5',
            text: '诊次成本核算',
            x: secondaryPositions[4].x,
            y: secondaryPositions[4].y,
            url: '/department?name=诊次/床日成本核算'
          },
          {
            id: 'sec6',
            text: '绩效二次分配',
            x: secondaryPositions[5].x,
            y: secondaryPositions[5].y,
            url: '/Overall?name=绩效二级分配'
          },
          {
            id: 'sec7',
            text: 'DRG/DIP疾病绩效',
            x: secondaryPositions[6].x,
            y: secondaryPositions[6].y,
            url: '/Overall?name=疾病能力绩效'
          },
          {
            id: 'sec8',
            text: '人力资源一体化',
            x: secondaryPositions[7].x,
            y: secondaryPositions[7].y,
            url: '/humanResources?name=产品概述'
          },
          {
            id: 'sec9',
            text: '设备·资产管理',
            x: secondaryPositions[8].x,
            y: secondaryPositions[8].y,
            url: '/equipAssets?name=智慧设备・资产管理平台'
          },
          {
            id: 'sec10',
            text: '中药饮片溯源',
            x: secondaryPositions[9].x,
            y: secondaryPositions[9].y,
            // url: '/supplyChain?name=中药饮片溯源平台(TSD+)'
            url:
                "/supplyChain?name=中药饮片溯源平台" +
                `${encodeURIComponent("(TSD+)")}`,
          },
          {
            id: 'sec11',
            text: '智慧供应链SPD',
            x: secondaryPositions[10].x,
            y: secondaryPositions[10].y,
            url: '/supplyChain?name=物资管理'
          },
          {
            id: 'sec12',
            text: '科研项目管控',
            x: secondaryPositions[11].x,
            y: secondaryPositions[11].y,
            url: '/bascm?name=专项经费管理'
          },
          {
            id: 'sec13',
            text: '合同生命周期管理',
            x: secondaryPositions[12].x,
            y: secondaryPositions[12].y,
            url: '/bascm?name=合同管理'
          },
          {
            id: 'sec14',
            text: '电子采购协同',
            x: secondaryPositions[13].x,
            y: secondaryPositions[13].y,
            url: '/supplyChain?name=电子采购平台(供应商协同)'
          },
          {
            id: 'sec15',
            text: '移动应用平台',
            x: secondaryPositions[14].x,
            y: secondaryPositions[14].y,
            url: '/hprSystem?name=HRP智慧管理平台'
          } // 默认为HRP平台入口，如有专属路径请替换
        ]
      }
    }
  },
  mounted() {
    // 初始化可视区域监听
    this.initIntersectionObserver();
  },
  beforeDestroy() {
    // 组件销毁时清理观察器
    if (this.observer) {
      this.observer.disconnect();
    }
  },
  computed: {
    /**
     * 中心节点样式
     * 使用 left/top 50% + transform 实现完美居中
     */
    getCenterNodeStyle() {
      return {
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)',
        width: '160px',
        height: '60px'
      };
    }
  },
  methods: {
    /**
     * 计算节点位置
     * @param {Object} node - 节点对象
     * @returns {Object} CSS样式对象
     */
    getNodePosition(node) {
      // 核心节点宽度160px，次级节点宽度140px
      const nodeWidth = node.id.includes('core') ? 160 : 140;
      const nodeHeight = 60;

      return {
        // calc(50% + offsetX) 从容器中心开始偏移
        left: `calc(50% + ${node.x}px)`,
        top: `calc(50% + ${node.y}px)`,
        // 节点自身居中
        transform: 'translate(-50%, -50%)',
        width: `${nodeWidth}px`,
        height: `${nodeHeight}px`
      };
    },


    /**
     * 鼠标移出效果
     * 恢复原始大小和层级
     */
    handleMouseLeave(e) {
      e.target.style.transform = 'translate(-50%, -50%) scale(1)';
      e.target.style.zIndex = '';
    },

    /**
     * 节点点击事件
     * @param {string} nodeId - 节点ID
     * @param {string} nodeText - 节点文本
     */
    handleNodeClick(item) {
      if (item.url) {
        this.$router.push(item.url)
      }
    },

    /**
     * 初始化Intersection Observer
     * 监听容器是否进入可视区域，用于触发入场动画
     */
    initIntersectionObserver() {
      // 浏览器兼容性检查
      if (!('IntersectionObserver' in window)) {
        // 不支持则直接触发动画
        setTimeout(() => this.animateNodes(), 500);
        return;
      }

      // 创建观察器实例
      this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              // 当容器进入可视区域且动画未启动时触发
              if (entry.isIntersecting && !this.animationStarted) {
                this.animationStarted = true;
                this.animateNodes();
                // 触发后停止观察，避免重复触发
                this.observer.unobserve(entry.target);
              }
            });
          },
          {
            threshold: 0.1,   // 10%可见时触发
            rootMargin: '50px' // 提前50px触发
          }
      );

      // 开始观察容器
      this.$nextTick(() => {
        const container = document.getElementById('mindmap-container');
        if (container) this.observer.observe(container);
      });
    },

    /**
     * 启动节点入场动画
     */
    animateNodes() {
      this.$nextTick(() => this.animateCenterNode());
    },

    /**
     * 第一层动画：中心节点
     */
    animateCenterNode() {
      const centerNode = this.$refs.centerNode;
      if (centerNode) {
        centerNode.classList.add('animate-center');
        // 0.5秒后开始核心节点动画
        setTimeout(() => this.animateCoreNodes(), 500);
      }
    },

    /**
     * 第二层动画：核心节点（5个）
     * 每个节点间隔150ms依次出现
     */
    animateCoreNodes() {
      this.nodeData.core.forEach((core, index) => {
        setTimeout(() => {
          const node = this.$refs[`node-${core.id}`];
          if (node && node[0]) node[0].classList.add('animate-in');
        }, index * 150);
      });

      // 1秒后开始次级节点动画
      setTimeout(() => this.animateSecondaryNodes(), 1000);
    },

    /**
     * 第三层动画：次级节点（15个）
     * 每个节点间隔60ms快速环绕出现
     */
    animateSecondaryNodes() {
      this.nodeData.secondary.forEach((sec, index) => {
        setTimeout(() => {
          const node = this.$refs[`node-${sec.id}`];
          if (node && node[0]) node[0].classList.add('animate-in');
        }, index * 60);
      });
    }
  }
};
</script>

<style lang="less" scoped>
.box {
  padding: @commonPadding;
  /* 使用项目定义的通用间距 */
}

.mindmap-page {
  font-family: 'Microsoft YaHei', sans-serif;
  background-color: @mainGrayColor4;
  /* 使用项目主题色 */
  height: 700px;
  /* 固定容器高度 */
  position: relative;
}

#mindmap-container {
  position: relative;
  width: 100%;
  height: 100%;
}

/* 节点通用样式 */
.node {
  position: absolute;
  border-radius: 30px;
  /* 大圆角设计 */
  text-align: center;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  /* 鼠标手型提示可点击 */
  user-select: none;
  /* 防止文本被选中 */
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  /* 基础阴影 */
  padding: 0 15px;
  border: 2px solid transparent;
  transition: all 0.2s ease;
  /* 平滑过渡效果 */
  opacity: 0;
  /* 初始隐藏，等待动画 */

  &:hover {
    transform: translate(-50%, -50%) scale(1.05) !important;
    z-index: 100;
  }

  /* 节点入场动画类 */

  &.animate-in {
    animation: scaleIn 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
  }
}

/* 中心节点样式 - 黄底黑字 */
#center {
  background-color: #FFD700;
  color: #000;
  width: 160px;
  height: 60px;
  font-size: 1.2em;
  z-index: 10;
  /* 最高层级 */
  border: 2px solid #FFC107;
  box-shadow: 0 4px 15px rgba(255, 193, 7, 0.3);
  transform: translate(-50%, -50%) scale(0);
  /* 初始缩放为0 */

  /* 中心节点专属动画 */

  &.animate-center {
    animation: centerAppear 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
  }
}

/* 核心功能节点样式 - 黑底白字 */
.core-node {
  background-color: #000;
  color: #fff;
  width: 160px;
  height: 60px;
  font-size: 1.1em;
  z-index: 5;
  /* 中间层级 */
  border: 2px solid #34495e;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  transform: translate(-50%, -50%) scale(0);
}

/* 次级功能节点样式 - 白底黑字 */
.secondary-node {
  background-color: #fff;
  color: #000;
  border: 2px solid #ddd;
  width: 140px;
  /* 比核心节点稍窄 */
  height: 60px;
  font-size: 1em;
  z-index: 3;
  /* 最低层级 */
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
  transform: translate(-50%, -50%) scale(0);
}

/* 普通节点缩放动画 */
@keyframes scaleIn {
  0% {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0);
  }

  70% {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1.1);
  }

  100% {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
}

/* 中心节点动画 */
@keyframes centerAppear {
  0% {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0);
  }

  70% {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1.1);
  }

  100% {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
}

/* 响应式调整 - 移动端适配 */
@media (max-width: 768px) {
  .node {
    width: 140px !important;
    height: 60px !important;
    font-size: 0.9em !important;
  }

  #center {
    width: 150px !important;
    height: 65px !important;
    font-size: 1.1em !important;
  }
}
</style>